import { useCallback, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getName } from "../../../Utils/Item";
import { projectsDeleteDialog, projectsDeleteStart } from "../../../Store/Actions";
import { selectDeletingProject, selectShowDeleteDialog } from "../../../Store/Selectors/Delete";
import { selectSelectedOrActiveProjectIds } from "../../../Store/Selectors/Projects";
import { useAuthenticationPreflight } from "../../Generic/Authentication";
import { BaseDialog } from "../../Generic/BaseDialog";
import { IWithTranslatorProps, WithTranslator } from "../../Generic/Translator";
import { ConfirmDialog } from "../ConfirmDialog";

export const Delete = WithTranslator((props: IWithTranslatorProps): JSX.Element => {
  const { translator } = props;
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmHidden, setIsConfirmHidden] = useState(true);
  const { preflightAuthentication } = useAuthenticationPreflight();

  const onDismiss = useCallback(() => {
    dispatch(projectsDeleteDialog(false));
  }, [dispatch]);

  const onConfirm = useCallback(async () => {
    setIsConfirmHidden(true);
    setIsOpen(true);
    await preflightAuthentication();
    dispatch(projectsDeleteStart());
  }, [dispatch, preflightAuthentication]);

  const onConfirmDismiss = useCallback(() => {
    setIsConfirmHidden(true);
    setIsOpen(false);
    dispatch(projectsDeleteDialog(false));
  }, [dispatch]);

  const deletingProject = useSelector(selectDeletingProject);
  const totalDeleteCount = useSelector(selectSelectedOrActiveProjectIds).length;
  const showDeleteDialog = useSelector(selectShowDeleteDialog);

  useLayoutEffect(() => {
    if (!showDeleteDialog) {
      setTimeout(() => {
        setIsConfirmHidden(true);
        setIsOpen(false);
      }, 250);
      return;
    }

    setIsConfirmHidden(false);
  }, [showDeleteDialog]);

  const title = deletingProject != null
    ? `${translator.get(`delete`, `Project delete`)}... - ${getName(deletingProject)}`
    : `${translator.get(`generic.done`, `Done`)}...`;

  return (
    <>
      <BaseDialog isOpen={isOpen} onDismiss={onDismiss} showCloseButton title={title}>
        <span>
          {deletingProject != null
            && translator.get(`delete.progress`, `Deleting project. The dialog will close when done.`)}
        </span>
      </BaseDialog>
      <ConfirmDialog
        hidden={isConfirmHidden}
        title={translator.get(`delete.confirm.title`, `Delete ${totalDeleteCount} project(s)?`, {
          amount: totalDeleteCount.toString()
        })}
        subText={translator.get(`delete.confirm.text`, `Please choose if you'd like to delete this/these project(s).`)}
        confirmButtonText={translator.get(`delete.confirm.yes`, `Yes`)}
        onConfirm={onConfirm}
        dismissButtonText={translator.get(`delete.confirm.no`, `No, please don't`)}
        onDismiss={onConfirmDismiss}
      />
    </>
  );
});
